import React, { useState } from 'react';
import { Megaphone, Instagram, TrendingUp, Users, Heart, BarChart3, Camera, Sparkles, Send, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CursorGlow from '../../components/CursorGlow';
import ScrollProgress from '../../components/ScrollProgress';

const SocialMediaService = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    platforms: '',
    budget: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast({
      title: "Talebiniz Alındı! 📱",
      description: "Sosyal medya uzmanlarımız 24 saat içinde sizinle iletişime geçecek.",
    });
    setFormData({ name: '', email: '', phone: '', company: '', platforms: '', budget: '', message: '' });
  };

  const features = [
    {
      icon: Instagram,
      title: 'Platform Yönetimi',
      description: 'Instagram, TikTok, LinkedIn, Twitter ve Facebook hesaplarınızın profesyonel yönetimi'
    },
    {
      icon: Camera,
      title: 'İçerik Üretimi',
      description: 'Markanıza özel görsel ve video içerik üretimi, hikaye tasarımları'
    },
    {
      icon: TrendingUp,
      title: 'Büyüme Stratejisi',
      description: 'Organik büyüme taktikleri ve takipçi artırma stratejileri'
    },
    {
      icon: BarChart3,
      title: 'Performans Analizi',
      description: 'Detaylı raporlama ve ROI takibi ile sürekli optimizasyon'
    }
  ];

  const services = [
    'Sosyal medya strateji planlaması',
    'İçerik takvimi oluşturma',
    'Görsel ve video içerik üretimi',
    'Topluluk yönetimi ve etkileşim',
    'Influencer marketing',
    'Sosyal medya reklam yönetimi',
    'Kriz yönetimi',
    'Aylık performans raporları'
  ];

  const platforms = [
    { name: 'Instagram', color: 'from-pink-500 to-purple-500' },
    { name: 'TikTok', color: 'from-cyan-400 to-pink-500' },
    { name: 'LinkedIn', color: 'from-blue-600 to-blue-400' },
    { name: 'Twitter/X', color: 'from-gray-700 to-gray-500' },
    { name: 'Facebook', color: 'from-blue-500 to-blue-700' },
    { name: 'YouTube', color: 'from-red-500 to-red-600' }
  ];

  const packages = [
    {
      name: 'Başlangıç',
      price: '₺15.000',
      period: '/ay',
      features: ['2 platform yönetimi', '12 paylaşım/ay', 'Temel raporlama', 'Topluluk yönetimi'],
      popular: false
    },
    {
      name: 'Profesyonel',
      price: '₺30.000',
      period: '/ay',
      features: ['4 platform yönetimi', '30 paylaşım/ay', 'Video içerik üretimi', 'Reklam yönetimi', 'Detaylı analitik'],
      popular: true
    },
    {
      name: 'Kurumsal',
      price: 'Özel',
      period: 'teklif',
      features: ['Tüm platformlar', 'Sınırsız içerik', 'Influencer kampanyaları', '7/24 destek', 'Özel strateji'],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-pink-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/10 border border-pink-500/30 rounded-full mb-6">
                <Megaphone className="w-4 h-4 text-pink-400 animate-pulse" />
                <span className="text-pink-400 text-sm font-medium">Sosyal Medya & Marka İletişimi</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Markanızı
                <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent"> Sosyal Medyada </span>
                Büyütün
              </h1>

              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Profesyonel sosyal medya yönetimi ile markanızın dijital varlığını güçlendirin. 
                İçerik stratejisi, topluluk yönetimi ve performans analizi bir arada.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-8 py-6 text-lg hover:scale-105 transition-all duration-300"
                  data-testid="service-cta-button"
                >
                  Ücretsiz Strateji Görüşmesi
                </Button>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=600&fit=crop"
                alt="Social Media Management"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-6 shadow-xl">
                <Heart className="w-8 h-8 text-white mb-2" />
                <div className="text-white font-bold text-2xl">3x</div>
                <div className="text-pink-100 text-sm">Etkileşim Artışı</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Neden <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">RUYAN</span>?
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-pink-500/50 transition-all duration-500 hover:-translate-y-2"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Yönettiğimiz <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Platformlar</span>
          </h2>

          <div className="flex flex-wrap justify-center gap-4">
            {platforms.map((platform, index) => (
              <div 
                key={index}
                className={`px-8 py-4 bg-gradient-to-r ${platform.color} rounded-2xl text-white font-semibold hover:scale-110 transition-transform cursor-pointer shadow-lg`}
              >
                {platform.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Hizmetlerimiz</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Markanızın sosyal medya ihtiyaçlarını eksiksiz karşılıyoruz.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0" />
                    <span className="text-gray-300">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1552581234-26160f608093?w=600&h=500&fit=crop"
                alt="Social Media Services"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Paketlerimiz</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                className={`relative bg-slate-800/40 backdrop-blur-sm border rounded-2xl p-8 transition-all hover:-translate-y-2 ${
                  pkg.popular ? 'border-pink-500/50 shadow-2xl shadow-pink-500/20' : 'border-slate-700/50 hover:border-pink-500/30'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-medium rounded-full">
                      En Popüler
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-white mb-2">{pkg.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                    {pkg.price}
                  </span>
                  <span className="text-gray-500">{pkg.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-pink-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`w-full py-6 rounded-xl font-semibold ${
                    pkg.popular 
                      ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white' 
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                >
                  Teklif Al
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section id="contact-form" className="py-20 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[800px] h-[800px] bg-pink-500/10 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-slate-800/60 backdrop-blur-xl border border-pink-500/30 rounded-3xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Sosyal Medya Stratejinizi
                <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent"> Birlikte Oluşturalım!</span>
              </h2>
              <p className="text-gray-400 text-lg">
                Ücretsiz strateji görüşmesi için formu doldurun
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 block">Adınız Soyadınız *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-900/50 border-pink-500/30 text-white h-12 rounded-xl focus:border-pink-500"
                    placeholder="Ahmet Yılmaz"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">E-posta *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-900/50 border-pink-500/30 text-white h-12 rounded-xl focus:border-pink-500"
                    placeholder="ahmet@sirket.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company" className="text-gray-300 mb-2 block">Şirket / Marka</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="bg-slate-900/50 border-pink-500/30 text-white h-12 rounded-xl focus:border-pink-500"
                    placeholder="Marka Adı"
                  />
                </div>
                <div>
                  <Label htmlFor="platforms" className="text-gray-300 mb-2 block">İlgilendiğiniz Platformlar</Label>
                  <Input
                    id="platforms"
                    value={formData.platforms}
                    onChange={(e) => setFormData({ ...formData, platforms: e.target.value })}
                    className="bg-slate-900/50 border-pink-500/30 text-white h-12 rounded-xl focus:border-pink-500"
                    placeholder="Instagram, TikTok..."
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-300 mb-2 block">Projeniz Hakkında</Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-slate-900/50 border-pink-500/30 text-white rounded-xl resize-none focus:border-pink-500"
                  placeholder="Hedefleriniz ve beklentileriniz neler?"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-6 text-lg font-semibold rounded-xl hover:scale-105 transition-all"
              >
                Ücretsiz Görüşme Talep Et
                <Send className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SocialMediaService;
